import axios from 'axios';
import { TokenService } from '../auth/TokenService';
import logger from '../../utils/logger';
import { handleApiError, withTimeout } from '../../utils/errorHandler';
import { showGlobalApiError, isApiErrorServiceInitialized } from '../apiErrorService';

// Create axios instance with default configuration
const client = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/prompt-back/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const shouldShowGlobalError = (status) => {
  const skipGlobalError = [
    401, // Authentication errors handled by redirect
    403, // Role authorization errors handled by modal
    404, 
    412, // Incorrect password errors handled by modal
    422  
  ];
  
  return !skipGlobalError.includes(status);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
client.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    
    // Log authentication status for debugging
    logger.auth('Request Authentication', {
      url: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      tokenValid: token ? TokenService.isValidToken(token) : false
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // request timestamp for debugging
    config.metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor
client.interceptors.response.use(
  (response) => {
    // Log response time in development
    if (response.config.metadata) {
      const endTime = new Date();
      const duration = endTime - response.config.metadata.startTime;
      logger.apiRequest(
        response.config.method || 'UNKNOWN',
        response.config.url || 'Unknown URL',
        duration,
        !!response.config.headers.Authorization
      );
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const { status, data } = error.response;
      
      if (status === 401 && !originalRequest._retry) {
        // Skip refresh for auth endpoints
        if (originalRequest.url.includes('/auth/login') || 
            originalRequest.url.includes('/auth/refresh')) {
          TokenService.removeTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // If we're already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Try to refresh the token
          const refreshToken = TokenService.getRefreshToken();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const refreshResponse = await axios.post(
            `${client.defaults.baseURL}/auth/refresh`,
            { refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data;
          
          // Store new tokens
          TokenService.setTokens(accessToken, newRefreshToken || refreshToken);
          
          // Process the failed queue
          processQueue(null, accessToken);
          
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return client(originalRequest);

        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          processQueue(refreshError, null);
          TokenService.removeTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      
      switch (status) {
        case 401:
          // token refresh failed or was skipped
          TokenService.removeTokens();
          window.location.href = '/login';
          break;
          
        case 403:
          // Forbidden: user doesn't have permission
          logger.warn('Access forbidden', { message: data.message || 'Insufficient permissions', url: error.config.url });
          break;
          
        case 404:
          // Not found
          logger.warn('Resource not found', { url: error.config.url });
          break;
          
        case 422:
          // Validation error
          logger.warn('Validation error', { errors: data.errors, message: data.message, url: error.config.url });
          break;
          
        case 429:
          // Rate limit exceeded
          logger.warn('Rate limit exceeded', { url: error.config.url, retryAfter: error.response.headers['retry-after'] });
          break;
          
        case 500:
          // Server error
          logger.error('Server error', { message: data.message || 'Internal server error', url: error.config.url });
          break;
          
        default:
          logger.error('API Error', { status, message: data.message || 'Unknown error occurred', url: error.config.url });
      }
      
      // additional information
      error.apiError = {
        status,
        message: data.message || 'An error occurred',
        errors: data.errors || null,
        timestamp: new Date().toISOString()
      };

      // Show global API error for certain status codes
      if (isApiErrorServiceInitialized() && shouldShowGlobalError(status)) {
        const apiError = handleApiError(error);
        showGlobalApiError(apiError, { level: 'page' });
      }
    } else if (error.request) {
      // Network error - no response received
      logger.error('Network error', { message: error.message, url: error.config?.url });
      error.apiError = {
        status: 0,
        message: 'Network error. Please check your connection.',
        errors: null,
        timestamp: new Date().toISOString()
      };

      // Show network errors globally
      if (isApiErrorServiceInitialized()) {
        const apiError = handleApiError(error);
        showGlobalApiError(apiError, { level: 'page' });
      }
    } else {
      // Request setup error
      logger.error('Request setup error', { message: error.message, url: error.config?.url });
      error.apiError = {
        status: 0,
        message: 'Request configuration error',
        errors: null,
        timestamp: new Date().toISOString()
      };

      // Show setup errors globally
      if (isApiErrorServiceInitialized()) {
        const apiError = handleApiError(error);
        showGlobalApiError(apiError, { level: 'page' });
      }
    }
    
    return Promise.reject(error);
  }
);


// Helper function to extract data from response
export const extractData = (response) => {
  return response.data;
};

// Helper function for GET requests
export const get = async (url, config = {}) => {
  try {
    const response = await withTimeout(client.get(url, config), config.timeout || 10000, `GET ${url}`);
    return extractData(response);
  } catch (error) {
    throw handleApiError(error, `GET ${url}`);
  }
};

// Helper function for POST requests
export const post = async (url, data = {}, config = {}) => {
  try {
    const response = await withTimeout(client.post(url, data, config), config.timeout || 10000, `POST ${url}`);
    return extractData(response);
  } catch (error) {
    throw handleApiError(error, `POST ${url}`);
  }
};

// Helper function for PUT requests
export const put = async (url, data = {}, config = {}) => {
  try {
    const response = await withTimeout(client.put(url, data, config), config.timeout || 10000, `PUT ${url}`);
    return extractData(response);
  } catch (error) {
    throw handleApiError(error, `PUT ${url}`);
  }
};

// Helper function for PATCH requests
export const patch = async (url, data = {}, config = {}) => {
  try {
    const response = await withTimeout(client.patch(url, data, config), config.timeout || 10000, `PATCH ${url}`);
    return extractData(response);
  } catch (error) {
    throw handleApiError(error, `PATCH ${url}`);
  }
};

// Helper function for DELETE requests
export const del = async (url, config = {}) => {
  try {
    const response = await withTimeout(client.delete(url, config), config.timeout || 10000, `DELETE ${url}`);
    return extractData(response);
  } catch (error) {
    throw handleApiError(error, `DELETE ${url}`);
  }
};

export default client;